import { ErrorResponse, RejectedValue } from "shared/interfaces/core-interfaces";
import { CommunicationListResponse, CreateCommunicationListRequest, CreateCommunicationListResponse, initialTimeTravelSliceValue, UpdateCommunicationListRequest, UpdateCommunicationListResponse } from "./communication-list-interfaces";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AuthHeader } from "services/authHeader";
import { initialSliceValue } from "features/ApplicantTracking/state/applicant-tracking-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

export const GetCommunicationList = createAsyncThunk<CommunicationListResponse[], void, { rejectValue: RejectedValue }>(
    'Slice/CommunicationListSlice/GetCommunicationList',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/communication-list/get-all', {
            method: "GET",
            headers: AuthHeader(),            
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CommunicationListResponse[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CreateCommunicationList = createAsyncThunk<CreateCommunicationListResponse, CreateCommunicationListRequest, { rejectValue: RejectedValue }>(
    'Slice/CommunicationListSlice/CreateCommunicationList',
    async (createCommunicationListRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/communication-list/create', {
            method: "POST",
            headers: AuthHeader(true, true),
            body: JSON.stringify(createCommunicationListRequest)   
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreateCommunicationListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const UpdateCommunicationList = createAsyncThunk<UpdateCommunicationListResponse, UpdateCommunicationListRequest, { rejectValue: RejectedValue }>(
    'Slice/CommunicationListSlice/UpdateCommunicationList',
    async (updateCommunicationListRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/communication-list/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(updateCommunicationListRequest)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UpdateCommunicationListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const CommunicationListSlice = createSlice({
    name: "CommunicationListSlice",
    initialState: initialTimeTravelSliceValue,
    reducers: {
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.isLoading = false;
            state.showNotification = false;
        },        
    },
    extraReducers(builder) {
        builder
            .addCase(CreateCommunicationList.fulfilled, (state, action: PayloadAction<CreateCommunicationListResponse>) => {
                state.isLoading = false;
                state.statusCode = "200";
                state.message = action.payload.message;
                state.showNotification = true;
            })
            .addCase(CreateCommunicationList.pending, (state) => {
                state.isLoading = true;
                state.statusCode = '';
                state.message = '';
                state.showNotification = false;
            })
            .addCase(CreateCommunicationList.rejected, (state, action) => {
                state.isLoading = false;
                state.statusCode = '500';
                state.message = action.payload?.message ?? 'An error occurred';
                state.showNotification = true;
            })
            .addCase(UpdateCommunicationList.pending, (state) => {
                state.isLoading = true;
                state.statusCode = '';
                state.message = '';
                state.showNotification = false;
            })
            .addCase(UpdateCommunicationList.fulfilled, (state, action: PayloadAction<UpdateCommunicationListResponse>) => {
                state.isLoading = false;
                state.statusCode = "200";
                state.message = action.payload.message;
                state.showNotification = true;
            })
            .addCase(UpdateCommunicationList.rejected, (state, action) => {
                state.isLoading = false;
                state.statusCode = '500';
                state.message = action.payload?.message ?? 'An error occurred';
                state.showNotification = true;
            })
    }
})

export const { resetStatusCode } = CommunicationListSlice.actions;
export default CommunicationListSlice.reducer;